import type { Component } from 'grapesjs';
import { createDefaultEditorSettings } from '../models/project-schema';
import type { EditingMode, ResponsiveIntentKind, WebKilnProject } from '../types';
import {
  INTENT_LABELS,
  INTENT_PLAIN_EXPLANATIONS,
  addCustomBreakpoint,
  getEffectiveIntents,
  intentClass,
  normalizeEditorSettings,
  resetBreakpoint,
  responsiveCss,
  setIntent,
  validateResponsive,
} from './responsive-intent';
import { WebKilnEditorAdapter } from './webkiln-editor-adapter';

const intentOrder: ResponsiveIntentKind[] = [
  'stack-below',
  'full-width-small',
  'hide-at-breakpoint',
  'reorder-mobile',
  'maintain-aspect',
  'responsive-type',
  'horizontal-scroll',
  'mobile-navigation',
  'prioritize-content',
  'accordion',
  'keep-beside',
  'preserve-custom',
];

export class EditingExperienceController {
  private mode: EditingMode;
  private breakpoint = 'mobile';
  private compare = false;
  private mobileChecked = false;

  constructor(
    private readonly adapter: WebKilnEditorAdapter,
    private readonly project: WebKilnProject,
    private readonly dirty: () => void,
  ) {
    this.project.editorSettings = normalizeEditorSettings(
      project.editorSettings ?? createDefaultEditorSettings(),
    );
    this.mode = this.project.editorSettings.mode;
  }

  start(): void {
    this.renderToolbar();
    this.renderGuidedPanel();
    this.bindMode();
    this.bindResponsiveControls();
    this.adapter.subscribe('select', () => {
      this.renderResponsivePanel();
      this.renderProPanel();
      this.renderGuidedPanel();
    });
    this.applyStyles();
    this.updateModeUi();
  }

  private renderToolbar(): void {
    const topbar = document.querySelector('.topbar');
    if (!topbar || document.querySelector('[data-editing-experience]')) return;
    const wrap = document.createElement('div');
    wrap.dataset.editingExperience = 'true';
    wrap.className = 'editing-experience-toolbar';
    wrap.innerHTML = `<label class="mode-picker">Mode<select id="editingMode" aria-label="Editing mode"><option value="guided">Guided</option><option value="standard">Standard</option><option value="pro">Pro</option></select></label><div class="breakpoint-picker" role="group" aria-label="Responsive breakpoint"><button type="button" data-breakpoint="desktop">Desktop</button><button type="button" data-breakpoint="laptop">Laptop</button><button type="button" data-breakpoint="tablet">Tablet</button><button type="button" data-breakpoint="mobile">Mobile</button><button type="button" data-compare="true">Compare</button></div><div class="custom-breakpoint-control"><input id="customBreakpointWidth" type="number" min="320" max="2400" step="1" placeholder="px" aria-label="Custom breakpoint width" /><button type="button" data-add-breakpoint title="Add custom breakpoint">+</button></div>`;
    const picker = wrap.querySelector<HTMLElement>('.breakpoint-picker');
    this.project
      .editorSettings!.breakpoints.filter((item) => item.id.startsWith('custom-'))
      .forEach((item) => this.appendBreakpointButton(picker, item));
    const menu = document.querySelector('.editor-more-menu');
    if (menu) menu.append(wrap);
    else topbar.insertBefore(wrap, topbar.querySelector('.top-actions'));
  }

  private renderGuidedPanel(): void {
    const panel = document.querySelector('#sitePanel');
    if (!panel) return;
    if (document.querySelector('.editor-shell')?.classList.contains('client-content-mode')) {
      panel.querySelector('[data-guided-panel]')?.remove();
      return;
    }
    let card = panel.querySelector<HTMLElement>('[data-guided-panel]');
    if (!card) {
      card = document.createElement('section');
      card.dataset.guidedPanel = 'true';
      card.className = 'guided-panel';
      panel.prepend(card);
    }

    const doc = this.adapter.getFrameDocument();
    const hasHeadline = Boolean(doc?.querySelector('h1, h2, [data-wk-headline]'));
    const hasAction = Boolean(doc?.querySelector('button, a.button, a[href]:not([href="#"])'));
    const allImages = Array.from(doc?.querySelectorAll('img') ?? []);
    const hasAlt = allImages.length === 0 || allImages.every((img) => Boolean(img.getAttribute('alt')?.trim()));
    const isMobileViewed = this.mobileChecked || this.breakpoint === 'mobile';

    const selected = this.adapter.getSelectedComponent() as Component | null;
    const tagName = String(selected?.get('tagName') || '').toLowerCase();
    const isContainer = Boolean(selected && (selected.components().length > 1 || ['div', 'section', 'main', 'article'].includes(tagName)));
    const isImage = tagName === 'img' || selected?.get('type') === 'image';
    const isText = ['h1', 'h2', 'h3', 'h4', 'p', 'span'].includes(tagName) || selected?.get('type') === 'text';
    const isNav = tagName === 'nav' || selected?.get('type') === 'navigation' || (selected?.getAttributes?.() ?? {})['role'] === 'navigation';

    let recommendationsHtml = '';
    if (isContainer) {
      recommendationsHtml = `
        <div class="guided-recommendation-group">
          <p class="guided-group-title">Container recommendations</p>
          <div class="guided-actions">
            <button type="button" data-guided-intent="stack-below" class="guided-action-btn">📱 Stack columns vertically on phones</button>
            <button type="button" data-guided-intent="keep-beside" class="guided-action-btn">↔ Keep side-by-side in row</button>
          </div>
        </div>`;
    } else if (isImage) {
      recommendationsHtml = `
        <div class="guided-recommendation-group">
          <p class="guided-group-title">Media recommendations</p>
          <div class="guided-actions">
            <button type="button" data-guided-intent="maintain-aspect" data-intent-value="16 / 9" class="guided-action-btn">🖼 Keep 16:9 widescreen ratio</button>
            <button type="button" data-guided-intent="maintain-aspect" data-intent-value="1 / 1" class="guided-action-btn">⏹ Square ratio (1:1)</button>
          </div>
        </div>`;
    } else if (isText) {
      recommendationsHtml = `
        <div class="guided-recommendation-group">
          <p class="guided-group-title">Typography recommendations</p>
          <div class="guided-actions">
            <button type="button" data-guided-intent="responsive-type" class="guided-action-btn">📏 Fluid responsive text sizing</button>
            <button type="button" data-guided-intent="full-width-small" class="guided-action-btn">↔ Full width on smaller screens</button>
          </div>
        </div>`;
    } else if (isNav) {
      recommendationsHtml = `
        <div class="guided-recommendation-group">
          <p class="guided-group-title">Navigation recommendations</p>
          <div class="guided-actions">
            <button type="button" data-guided-intent="mobile-navigation" class="guided-action-btn">🍔 Convert navigation into mobile drawer</button>
          </div>
        </div>`;
    } else {
      recommendationsHtml = `
        <div class="guided-recommendation-group">
          <p class="guided-group-title">General recommendations</p>
          <div class="guided-actions">
            <button type="button" data-guided-intent="stack-below" class="guided-action-btn">📱 Stack elements on mobile</button>
            <button type="button" data-guided-action="check" class="guided-action-btn">🔍 Check layout & accessibility</button>
          </div>
        </div>`;
    }

    card.innerHTML = `
      <div class="guided-head">
        <p class="eyebrow">Guided editing</p>
        <h3>Make the next good decision</h3>
        <p class="panel-note">WebKiln keeps layout safe and automatically adapts your choices across every screen size.</p>
      </div>
      <div class="content-checklist" aria-label="Content checklist">
        <p class="guided-checklist-title">Content completeness</p>
        <div class="checklist-item ${hasHeadline ? 'is-complete' : 'is-pending'}">
          <span class="checklist-badge">${hasHeadline ? '✓' : '○'}</span>
          <span>Clear primary headline (H1)</span>
        </div>
        <div class="checklist-item ${hasAction ? 'is-complete' : 'is-pending'}">
          <span class="checklist-badge">${hasAction ? '✓' : '○'}</span>
          <span>Primary call to action button</span>
        </div>
        <div class="checklist-item ${hasAlt ? 'is-complete' : 'is-pending'}">
          <span class="checklist-badge">${hasAlt ? '✓' : '○'}</span>
          <span>Image accessibility descriptions</span>
        </div>
        <div class="checklist-item ${isMobileViewed ? 'is-complete' : 'is-pending'}">
          <span class="checklist-badge">${isMobileViewed ? '✓' : '○'}</span>
          <span>Mobile screen preview checked</span>
        </div>
      </div>
      ${recommendationsHtml}
    `;

    card.querySelectorAll<HTMLButtonElement>('[data-guided-intent]').forEach((button) => {
      button.addEventListener('click', () => {
        const intent = button.dataset.guidedIntent as ResponsiveIntentKind;
        const val = button.dataset.intentValue;
        this.setSelectedIntent(intent, true, val);
      });
    });

    card.querySelector('[data-guided-action="check"]')?.addEventListener('click', () => {
      document.querySelector('#responsiveWarnings')?.scrollIntoView({ behavior: 'smooth' });
    });
  }

  private bindMode(): void {
    document
      .querySelector<HTMLSelectElement>('#editingMode')
      ?.addEventListener('change', (event) => {
        this.mode = (event.target as HTMLSelectElement).value as EditingMode;
        this.project.editorSettings = { ...this.project.editorSettings!, mode: this.mode };
        this.updateModeUi();
        this.renderProPanel();
        this.renderGuidedPanel();
        this.dirty();
      });
  }

  private bindResponsiveControls(): void {
    document
      .querySelectorAll<HTMLButtonElement>('[data-breakpoint]')
      .forEach((button) => this.bindBreakpointButton(button));
    document.querySelector('[data-add-breakpoint]')?.addEventListener('click', () => {
      const input = document.querySelector<HTMLInputElement>('#customBreakpointWidth');
      try {
        const result = addCustomBreakpoint(
          this.project.editorSettings!,
          `Custom ${input?.value ?? ''}px`,
          Number(input?.value),
        );
        this.project.editorSettings = result.settings;
        const picker = document.querySelector<HTMLElement>('.breakpoint-picker');
        const button =
          Array.from(picker?.querySelectorAll<HTMLButtonElement>('[data-breakpoint]') ?? []).find(
            (item) => item.dataset.breakpoint === result.breakpoint.id,
          ) ?? this.appendBreakpointButton(picker, result.breakpoint);
        this.bindBreakpointButton(button);
        this.selectBreakpoint(button, result.breakpoint.id, result.breakpoint.width);
        this.dirty();
      } catch (error) {
        window.alert(error instanceof Error ? error.message : 'Could not add breakpoint.');
      }
    });
    document.querySelector('[data-compare]')?.addEventListener('click', (event) => {
      this.compare = !this.compare;
      (event.currentTarget as HTMLElement).classList.toggle('active', this.compare);
      document.body.classList.toggle('responsive-compare', this.compare);
    });
  }

  private appendBreakpointButton(
    picker: HTMLElement | null,
    breakpoint: { id: string; label: string; width: number },
  ): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.breakpoint = breakpoint.id;
    button.dataset.breakpointWidth = String(breakpoint.width);
    button.textContent = breakpoint.label;
    picker?.insertBefore(button, picker.querySelector('[data-compare]') ?? null);
    return button;
  }

  private bindBreakpointButton(button: HTMLButtonElement): void {
    button.addEventListener('click', () =>
      this.selectBreakpoint(
        button,
        button.dataset.breakpoint ?? 'mobile',
        Number(button.dataset.breakpointWidth),
      ),
    );
  }

  private selectBreakpoint(button: HTMLButtonElement, id: string, width: number): void {
    this.breakpoint = id;
    if (id === 'mobile') this.mobileChecked = true;
    document
      .querySelectorAll('[data-breakpoint]')
      .forEach((item) => item.classList.toggle('active', item === button));
    if (id.startsWith('custom-')) this.adapter.setCustomDevice(id, width);
    else this.adapter.setDevice(id as 'desktop' | 'laptop' | 'tablet' | 'mobile');
    this.renderResponsivePanel();
    this.renderGuidedPanel();
  }

  private renderResponsivePanel(): void {
    const inspector = document.querySelector('.inspector');
    if (!inspector) return;
    inspector.querySelector('[data-responsive-panel]')?.remove();
    if (document.querySelector('.editor-shell')?.classList.contains('client-content-mode')) {
      return;
    }
    const selected = this.adapter.getSelectedComponent() as Component | null;
    const id = this.adapter.getComponentId(selected);
    if (!id) return;
    const settings = this.project.editorSettings!;
    const metadata = settings.responsiveIntents[id] ?? { intents: [] };
    const effectiveIntents = getEffectiveIntents(metadata, settings.breakpoints, this.breakpoint);

    const section = document.createElement('section');
    section.dataset.responsivePanel = 'true';
    section.className = 'property-group responsive-panel';

    const intentRows = intentOrder
      .map((kind) => {
        const effective = effectiveIntents.find((item) => item.kind === kind);
        const isChecked = Boolean(effective?.enabled);
        let badgeHtml = '';
        if (isChecked) {
          if (effective?.isOverride) {
            badgeHtml = '<span class="intent-badge override" title="Overridden for this breakpoint">Override</span>';
          } else if (effective?.inheritedFrom) {
            badgeHtml = `<span class="intent-badge inherited" title="Inherited from ${effective.inheritedFrom}">From ${effective.inheritedFrom}</span>`;
          } else {
            badgeHtml = '<span class="intent-badge base" title="Base breakpoint value">Base</span>';
          }
        }
        const explanation = INTENT_PLAIN_EXPLANATIONS[kind] ?? '';

        return `
          <div class="intent-item">
            <label class="intent-row">
              <input type="checkbox" data-intent="${kind}" ${isChecked ? 'checked' : ''} />
              <span class="intent-label-wrap">
                <span class="intent-title">${INTENT_LABELS[kind]}</span>
                ${badgeHtml}
              </span>
            </label>
            <p class="intent-explanation">${explanation}</p>
          </div>`;
      })
      .join('');

    const currentBpObj = settings.breakpoints.find((item) => item.id === this.breakpoint);
    const componentMetrics = selected
      ? {
        width: Number.parseInt(String(selected.getStyle()?.['width'] ?? '44'), 10) || 44,
        height: Number.parseInt(String(selected.getStyle()?.['height'] ?? '44'), 10) || 44,
        tagName: String(selected.get('tagName') || '').toLowerCase(),
        fontSize: Number.parseInt(String(selected.getStyle()?.['font-size'] ?? '16'), 10) || 16,
      }
      : undefined;

    const warnings = validateResponsive(metadata, componentMetrics, currentBpObj);

    section.innerHTML = `
      <div class="responsive-panel-head">
        <h3>Responsive intent · ${this.breakpoint}</h3>
        <p class="field-help">Choose layout adaptivity for smaller screens. Settings inherit from wider screens until overridden.</p>
      </div>
      <div class="intent-list">${intentRows}</div>
      <div class="responsive-panel-actions">
        <button type="button" class="ghost-btn" data-reset-breakpoint>Reset to inherited</button>
      </div>
      <div id="responsiveWarnings" class="responsive-warnings">
        ${warnings
        .map(
          (warning) => `
          <div class="warning-card warning-${warning.kind}">
            <span class="warning-icon">⚠</span>
            <div class="warning-body">
              <strong>${warning.message}</strong>
              ${warning.suggestion ? `<small>${warning.suggestion}</small>` : ''}
            </div>
          </div>`,
        )
        .join('')}
      </div>
    `;

    inspector.append(section);

    section.querySelectorAll<HTMLInputElement>('[data-intent]').forEach((input) =>
      input.addEventListener('change', () =>
        this.setSelectedIntent(input.dataset.intent as ResponsiveIntentKind, input.checked),
      ),
    );

    section.querySelector('[data-reset-breakpoint]')?.addEventListener('click', () => {
      this.project.editorSettings = resetBreakpoint(
        this.project.editorSettings!,
        id,
        this.breakpoint,
      );
      this.applyStyles();
      this.dirty();
      this.renderResponsivePanel();
    });
  }

  private renderProPanel(): void {
    const inspector = document.querySelector('.inspector');
    if (!inspector) return;
    inspector.querySelector('[data-pro-panel]')?.remove();
    if (
      this.mode !== 'pro' ||
      document.querySelector('.editor-shell')?.classList.contains('client-content-mode')
    ) {
      return;
    }

    const selected = this.adapter.getSelectedComponent() as Component | null;
    if (!selected) return;

    const styles = this.adapter.getComponentStyles(selected);
    const section = document.createElement('section');
    section.dataset.proPanel = 'true';
    section.className = 'property-group pro-panel';

    const display = styles['display'] ?? 'block';
    const flexDirection = styles['flex-direction'] ?? 'row';
    const justifyContent = styles['justify-content'] ?? 'flex-start';
    const alignItems = styles['align-items'] ?? 'stretch';
    const flexWrap = styles['flex-wrap'] ?? 'nowrap';
    const gap = styles['gap'] ?? '0px';

    const gridTemplateColumns = styles['grid-template-columns'] ?? '1fr';
    const position = styles['position'] ?? 'static';
    const top = styles['top'] ?? 'auto';
    const right = styles['right'] ?? 'auto';
    const bottom = styles['bottom'] ?? 'auto';
    const left = styles['left'] ?? 'auto';
    const zIndex = styles['z-index'] ?? 'auto';
    const overflowX = styles['overflow-x'] ?? 'visible';
    const overflowY = styles['overflow-y'] ?? 'visible';

    const tokens = Object.entries(this.project.themeTokens ?? {});

    section.innerHTML = `
      <div class="pro-head">
        <p class="eyebrow">Pro controls</p>
        <h3>Advanced layout & CSS</h3>
      </div>
      <div class="pro-grid-layout">
        <label class="field pro-field">
          <span>Display</span>
          <select id="proDisplay">
            <option value="block" ${display === 'block' ? 'selected' : ''}>Block</option>
            <option value="flex" ${display === 'flex' ? 'selected' : ''}>Flexbox</option>
            <option value="grid" ${display === 'grid' ? 'selected' : ''}>CSS Grid</option>
            <option value="inline-flex" ${display === 'inline-flex' ? 'selected' : ''}>Inline-Flex</option>
            <option value="none" ${display === 'none' ? 'selected' : ''}>None (Hidden)</option>
          </select>
        </label>
      </div>

      <div id="proFlexControls" class="pro-subgroup" style="${display.includes('flex') ? '' : 'display:none;'}">
        <p class="pro-subgroup-title">Flexbox alignment</p>
        <div class="pro-row">
          <label class="field pro-field">
            <span>Direction</span>
            <select id="proFlexDir">
              <option value="row" ${flexDirection === 'row' ? 'selected' : ''}>Row (Horizontal)</option>
              <option value="column" ${flexDirection === 'column' ? 'selected' : ''}>Column (Vertical)</option>
              <option value="row-reverse" ${flexDirection === 'row-reverse' ? 'selected' : ''}>Row Reverse</option>
              <option value="column-reverse" ${flexDirection === 'column-reverse' ? 'selected' : ''}>Column Reverse</option>
            </select>
          </label>
          <label class="field pro-field">
            <span>Wrap</span>
            <select id="proFlexWrap">
              <option value="nowrap" ${flexWrap === 'nowrap' ? 'selected' : ''}>No Wrap</option>
              <option value="wrap" ${flexWrap === 'wrap' ? 'selected' : ''}>Wrap</option>
            </select>
          </label>
        </div>
        <div class="pro-row">
          <label class="field pro-field">
            <span>Justify Content</span>
            <select id="proJustify">
              <option value="flex-start" ${justifyContent === 'flex-start' ? 'selected' : ''}>Start</option>
              <option value="center" ${justifyContent === 'center' ? 'selected' : ''}>Center</option>
              <option value="flex-end" ${justifyContent === 'flex-end' ? 'selected' : ''}>End</option>
              <option value="space-between" ${justifyContent === 'space-between' ? 'selected' : ''}>Space Between</option>
              <option value="space-around" ${justifyContent === 'space-around' ? 'selected' : ''}>Space Around</option>
            </select>
          </label>
          <label class="field pro-field">
            <span>Align Items</span>
            <select id="proAlign">
              <option value="stretch" ${alignItems === 'stretch' ? 'selected' : ''}>Stretch</option>
              <option value="flex-start" ${alignItems === 'flex-start' ? 'selected' : ''}>Start</option>
              <option value="center" ${alignItems === 'center' ? 'selected' : ''}>Center</option>
              <option value="flex-end" ${alignItems === 'flex-end' ? 'selected' : ''}>End</option>
            </select>
          </label>
        </div>
        <label class="field pro-field">
          <span>Gap</span>
          <input type="text" id="proGap" value="${gap}" placeholder="e.g. 16px or 1rem" />
        </label>
      </div>

      <div id="proGridControls" class="pro-subgroup" style="${display === 'grid' ? '' : 'display:none;'}">
        <p class="pro-subgroup-title">CSS Grid tracks</p>
        <label class="field pro-field">
          <span>Columns template</span>
          <select id="proGridColsPreset">
            <option value="1fr" ${gridTemplateColumns === '1fr' ? 'selected' : ''}>1 Column (1fr)</option>
            <option value="1fr 1fr" ${gridTemplateColumns === '1fr 1fr' ? 'selected' : ''}>2 Columns (1fr 1fr)</option>
            <option value="repeat(3, 1fr)" ${gridTemplateColumns === 'repeat(3, 1fr)' ? 'selected' : ''}>3 Columns</option>
            <option value="repeat(4, 1fr)" ${gridTemplateColumns === 'repeat(4, 1fr)' ? 'selected' : ''}>4 Columns</option>
            <option value="repeat(auto-fit, minmax(240px, 1fr))" ${gridTemplateColumns === 'repeat(auto-fit, minmax(240px, 1fr))' ? 'selected' : ''}>Responsive Auto-fit</option>
            <option value="custom">Custom definition...</option>
          </select>
        </label>
        <input type="text" id="proGridColsCustom" value="${gridTemplateColumns}" placeholder="e.g. 200px 1fr" />
      </div>

      <div class="pro-subgroup">
        <p class="pro-subgroup-title">Positioning & Layering</p>
        <div class="pro-row">
          <label class="field pro-field">
            <span>Position</span>
            <select id="proPosition">
              <option value="static" ${position === 'static' ? 'selected' : ''}>Static</option>
              <option value="relative" ${position === 'relative' ? 'selected' : ''}>Relative</option>
              <option value="absolute" ${position === 'absolute' ? 'selected' : ''}>Absolute</option>
              <option value="sticky" ${position === 'sticky' ? 'selected' : ''}>Sticky</option>
              <option value="fixed" ${position === 'fixed' ? 'selected' : ''}>Fixed</option>
            </select>
          </label>
          <label class="field pro-field">
            <span>Z-Index</span>
            <input type="text" id="proZIndex" value="${zIndex}" placeholder="auto" />
          </label>
        </div>
        <div class="pro-offsets-grid">
          <label class="field pro-field"><span>Top</span><input id="proTop" value="${top}" placeholder="auto" /></label>
          <label class="field pro-field"><span>Right</span><input id="proRight" value="${right}" placeholder="auto" /></label>
          <label class="field pro-field"><span>Bottom</span><input id="proBottom" value="${bottom}" placeholder="auto" /></label>
          <label class="field pro-field"><span>Left</span><input id="proLeft" value="${left}" placeholder="auto" /></label>
        </div>
      </div>

      <div class="pro-subgroup">
        <p class="pro-subgroup-title">Overflow handling</p>
        <div class="pro-row">
          <label class="field pro-field">
            <span>Overflow X</span>
            <select id="proOverflowX">
              <option value="visible" ${overflowX === 'visible' ? 'selected' : ''}>Visible</option>
              <option value="hidden" ${overflowX === 'hidden' ? 'selected' : ''}>Hidden</option>
              <option value="auto" ${overflowX === 'auto' ? 'selected' : ''}>Auto</option>
              <option value="scroll" ${overflowX === 'scroll' ? 'selected' : ''}>Scroll</option>
            </select>
          </label>
          <label class="field pro-field">
            <span>Overflow Y</span>
            <select id="proOverflowY">
              <option value="visible" ${overflowY === 'visible' ? 'selected' : ''}>Visible</option>
              <option value="hidden" ${overflowY === 'hidden' ? 'selected' : ''}>Hidden</option>
              <option value="auto" ${overflowY === 'auto' ? 'selected' : ''}>Auto</option>
              <option value="scroll" ${overflowY === 'scroll' ? 'selected' : ''}>Scroll</option>
            </select>
          </label>
        </div>
      </div>

      ${tokens.length
        ? `
      <div class="pro-subgroup">
        <p class="pro-subgroup-title">Theme tokens (CSS variables)</p>
        <div class="pro-token-row">
          <select id="proTokenSelect" class="pro-token-select">
            <option value="">Select project token...</option>
            ${tokens.map(([name, val]) => `<option value="var(--wk-${name})">--wk-${name} (${val})</option>`).join('')}
          </select>
          <button type="button" class="mini-btn" id="proApplyTokenBg" title="Apply as background">BG</button>
          <button type="button" class="mini-btn" id="proApplyTokenColor" title="Apply as text color">Text</button>
        </div>
      </div>`
        : ''
      }

      <div class="pro-subgroup">
        <p class="pro-subgroup-title">Sandboxed markup boundary</p>
        <div class="pro-code-preview" readonly tabindex="0">
          <code>${this.getSanitizedComponentMarkup(selected)}</code>
        </div>
        <p class="field-help">Custom markup conforms to WebKiln sandboxed boundary (executable scripts stripped).</p>
      </div>
    `;

    inspector.append(section);

    const updateStyle = (key: string, value: string) => {
      this.adapter.updateStyles(selected, { [key]: value });
      this.dirty();
    };

    section.querySelector<HTMLSelectElement>('#proDisplay')?.addEventListener('change', (e) => {
      const val = (e.target as HTMLSelectElement).value;
      updateStyle('display', val);
      const flexWrapEl = section.querySelector<HTMLElement>('#proFlexControls');
      const gridWrapEl = section.querySelector<HTMLElement>('#proGridControls');
      if (flexWrapEl) flexWrapEl.style.display = val.includes('flex') ? '' : 'none';
      if (gridWrapEl) gridWrapEl.style.display = val === 'grid' ? '' : 'none';
    });

    section.querySelector<HTMLSelectElement>('#proFlexDir')?.addEventListener('change', (e) => {
      updateStyle('flex-direction', (e.target as HTMLSelectElement).value);
    });
    section.querySelector<HTMLSelectElement>('#proFlexWrap')?.addEventListener('change', (e) => {
      updateStyle('flex-wrap', (e.target as HTMLSelectElement).value);
    });
    section.querySelector<HTMLSelectElement>('#proJustify')?.addEventListener('change', (e) => {
      updateStyle('justify-content', (e.target as HTMLSelectElement).value);
    });
    section.querySelector<HTMLSelectElement>('#proAlign')?.addEventListener('change', (e) => {
      updateStyle('align-items', (e.target as HTMLSelectElement).value);
    });
    section.querySelector<HTMLInputElement>('#proGap')?.addEventListener('change', (e) => {
      updateStyle('gap', (e.target as HTMLInputElement).value);
    });

    section.querySelector<HTMLSelectElement>('#proGridColsPreset')?.addEventListener('change', (e) => {
      const val = (e.target as HTMLSelectElement).value;
      const customInput = section.querySelector<HTMLInputElement>('#proGridColsCustom');
      if (val !== 'custom') {
        if (customInput) customInput.value = val;
        updateStyle('grid-template-columns', val);
      }
    });
    section.querySelector<HTMLInputElement>('#proGridColsCustom')?.addEventListener('change', (e) => {
      updateStyle('grid-template-columns', (e.target as HTMLInputElement).value);
    });

    section.querySelector<HTMLSelectElement>('#proPosition')?.addEventListener('change', (e) => {
      updateStyle('position', (e.target as HTMLSelectElement).value);
    });
    section.querySelector<HTMLInputElement>('#proZIndex')?.addEventListener('change', (e) => {
      updateStyle('z-index', (e.target as HTMLInputElement).value);
    });
    section.querySelector<HTMLInputElement>('#proTop')?.addEventListener('change', (e) => {
      updateStyle('top', (e.target as HTMLInputElement).value);
    });
    section.querySelector<HTMLInputElement>('#proRight')?.addEventListener('change', (e) => {
      updateStyle('right', (e.target as HTMLInputElement).value);
    });
    section.querySelector<HTMLInputElement>('#proBottom')?.addEventListener('change', (e) => {
      updateStyle('bottom', (e.target as HTMLInputElement).value);
    });
    section.querySelector<HTMLInputElement>('#proLeft')?.addEventListener('change', (e) => {
      updateStyle('left', (e.target as HTMLInputElement).value);
    });

    section.querySelector<HTMLSelectElement>('#proOverflowX')?.addEventListener('change', (e) => {
      updateStyle('overflow-x', (e.target as HTMLSelectElement).value);
    });
    section.querySelector<HTMLSelectElement>('#proOverflowY')?.addEventListener('change', (e) => {
      updateStyle('overflow-y', (e.target as HTMLSelectElement).value);
    });

    section.querySelector('#proApplyTokenBg')?.addEventListener('click', () => {
      const token = section.querySelector<HTMLSelectElement>('#proTokenSelect')?.value;
      if (token) updateStyle('background-color', token);
    });
    section.querySelector('#proApplyTokenColor')?.addEventListener('click', () => {
      const token = section.querySelector<HTMLSelectElement>('#proTokenSelect')?.value;
      if (token) updateStyle('color', token);
    });
  }

  private getSanitizedComponentMarkup(component: Component): string {
    const raw = component.toHTML ? component.toHTML() : '';
    return raw
      .replace(/<\/?(script|iframe|object|embed|applet)[\s\S]*?>/gi, '')
      .replace(/\s+on[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
      .replace(/\s+javascript\s*:/gi, '')
      .slice(0, 300)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private setSelectedIntent(kind: ResponsiveIntentKind, enabled = true, value?: string): void {
    const id = this.adapter.getComponentId(this.adapter.getSelectedComponent() as Component | null);
    if (!id) return;
    const component = this.adapter.getSelectedComponent() as Component | null;
    component?.addClass(intentClass(id));
    this.project.editorSettings = setIntent(this.project.editorSettings!, id, {
      kind,
      enabled,
      breakpoint: this.breakpoint,
      source: this.mode,
      ...(value ? { value } : {}),
    });
    this.applyStyles();
    this.dirty();
    this.renderResponsivePanel();
  }

  private applyStyles(): void {
    const settings = this.project.editorSettings!;
    const css = Object.entries(settings.responsiveIntents)
      .map(([id, metadata]) => responsiveCss(id, metadata, settings.breakpoints))
      .filter(Boolean)
      .join('\n');
    if (css) this.adapter.addResponsiveStyles(css);
  }

  private updateModeUi(): void {
    document.documentElement.dataset.editingMode = this.mode;
    const select = document.querySelector<HTMLSelectElement>('#editingMode');
    if (select) select.value = this.mode;
    document.querySelector('.inspector')?.classList.toggle('guided-mode', this.mode === 'guided');
    document.querySelector('.inspector')?.classList.toggle('pro-mode', this.mode === 'pro');
    const description = document.querySelector('#modeDescription');
    if (description)
      description.textContent =
        this.mode === 'guided'
          ? 'Simple controls and safe recommendations'
          : this.mode === 'pro'
            ? 'Full layout, code, and CSS controls'
            : 'Visual editing with responsive controls';
  }
}
